import React, { createContext, useContext, useState, useEffect } from 'react';
import { dataFetch } from '../api';

const FitnessContext = createContext();

export const useFitnessContext = () => {
  const context = useContext(FitnessContext);
  if (!context) throw new Error('useFitnessContext must be used within FitnessProvider');
  return context;
};

const getLocalDateString = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const FitnessProvider = ({ children }) => {
  const [waterIntakeHistory, setWaterIntakeHistory] = useState([]);
  const [waterGoal, setWaterGoalState] = useState(64);
  const [goalId, setGoalId] = useState(null);

  useEffect(() => {
    const loadFitnessData = async () => {
      const userId = localStorage.getItem('user_id');
      if (!userId) return;
      
      const resLogs = await dataFetch(`read/hydration_logs?owner_id=${userId}`);
      if (resLogs?.status === 'success') {
        setWaterIntakeHistory(resLogs.data ?? []);
      }

      const resGoal = await dataFetch(`read/hydration_goals?owner_id=${userId}`);
      if (resGoal?.status === 'success' && resGoal.data?.length > 0) {
        setWaterGoalState(resGoal.data[0].daily_goal);
        setGoalId(resGoal.data[0].id);
      }
    };
    loadFitnessData();
  }, []);

  const setWaterGoal = async (newGoal) => {
    setWaterGoalState(newGoal);
    const userId = localStorage.getItem('user_id');
    if (!userId) return;

    if (goalId) {
      await dataFetch(`update/hydration_goals/${goalId}?owner_id=${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ daily_goal: newGoal, user_id: userId })
      });
    } else {
      const res = await dataFetch(`create/hydration_goals?owner_id=${userId}`, {
        method: 'POST',
        body: JSON.stringify({ daily_goal: newGoal, user_id: userId })
      });
      if (res?.status === 'success') {
        setGoalId(res.data.id);
      }
    }
  };

  const getIntakeForDate = (dateStr) => {
    const record = waterIntakeHistory.find(record => record.date === dateStr);
    return record ? Number(record.amount) : 0;
  };

  const updateWaterIntake = async (date, ounces) => {
    const userId = localStorage.getItem('user_id');
    const existingIndex = waterIntakeHistory.findIndex(record => record.date === date);
    const numAmount = Math.max(0, ounces);
    
    if (existingIndex >= 0) {
      const existing = waterIntakeHistory[existingIndex];
      const res = await dataFetch(`update/hydration_logs/${existing.id}?owner_id=${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ amount: numAmount, date, user_id: userId })
      });
      if (res?.status === 'success') {
        setWaterIntakeHistory(prev => {
          const updated = [...prev];
          updated[existingIndex] = res.data;
          return updated;
        });
      }
    } else {
      const res = await dataFetch(`create/hydration_logs?owner_id=${userId}`, {
        method: 'POST',
        body: JSON.stringify({ amount: numAmount, date, user_id: userId })
      });
      if (res?.status === 'success') {
        setWaterIntakeHistory(prev => [...prev, res.data]);
      }
    }
  };

  const addWater = (amount, date = getLocalDateString()) => {
    const numAmount = parseFloat(amount);
    const currentIntake = getIntakeForDate(date);
    if (numAmount > 0 && currentIntake + numAmount <= 500) {
      const newTotal = Math.round((currentIntake + numAmount) * 10) / 10;
      updateWaterIntake(date, newTotal);
    }
  };

  const removeWater = (amount, date = getLocalDateString()) => {
    const numAmount = parseFloat(amount);
    const currentIntake = getIntakeForDate(date);
    if (numAmount > 0 && currentIntake - numAmount >= 0) {
      const newTotal = Math.round((currentIntake - numAmount) * 10) / 10;
      updateWaterIntake(date, newTotal);
    }
  };

  const resetIntakeForDate = (date) => {
    updateWaterIntake(date, 0);
  };

  const getWeeklyData = () => {
    const today = new Date();
    const weekData = [];
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = getLocalDateString(date);
      const record = waterIntakeHistory.find(r => r.date === dateStr);
      weekData.push({
        day: days[date.getDay()],
        ounces: record ? Number(record.amount) : 0,
        date: dateStr
      });
    }
    return weekData;
  };

  const getMonthlyTrend = () => {
    const today = new Date();
    const weeklyAverages = [];

    for (let week = 3; week >= 0; week--) {
      let totalOunces = 0;
      let daysWithData = 0;

      for (let day = 0; day < 7; day++) {
        const date = new Date(today);
        date.setDate(date.getDate() - (week * 7 + day));
        const dateStr = getLocalDateString(date);
        const record = waterIntakeHistory.find(r => r.date === dateStr);
        if (record) {
          totalOunces += Number(record.amount);
          daysWithData++;
        }
      }

      const avgOunces = daysWithData > 0 ? Math.round(totalOunces / daysWithData) : 0;
      weeklyAverages.push({ week: `Week ${4 - week}`, avgOunces });
    }
    return weeklyAverages;
  };

  const getStats = () => {
    const totalDays = waterIntakeHistory.length;
    const goalsReached = waterIntakeHistory.filter(r => Number(r.amount) >= waterGoal).length;
    
    let currentStreak = 0;
    const today = new Date();
    
    for (let i = 0; i < 365; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(today.getDate() - i);
      const dateStr = getLocalDateString(checkDate);
      const record = waterIntakeHistory.find(r => r.date === dateStr);
      
      const metGoal = record && Number(record.amount) >= waterGoal;
      
      if (metGoal) {
        currentStreak++;
      } else {
        if (i === 0) {
          continue;
        } else {
          break;
        }
      }
    }

    const totalOunces = waterIntakeHistory.reduce((sum, r) => sum + Number(r.amount), 0);
    const avgWaterIntake = totalDays > 0 ? parseFloat((totalOunces / totalDays).toFixed(1)) : 0;

    return { avgWaterIntake, daysTracked: totalDays, goalsReached, currentStreak };
  };

  return (
    <FitnessContext.Provider
      value={{
        waterGoal,
        setWaterGoal,
        getIntakeForDate,
        waterIntakeHistory,
        addWater,
        removeWater,
        resetIntakeForDate,
        getWeeklyData,
        getMonthlyTrend,
        getStats,
        getLocalDateString
      }}
    >
      {children}
    </FitnessContext.Provider>
  );
};